import { Radio, type RadioChangeEvent, Select } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { useMemo, useState } from 'react';

import { Flex, VFlex } from '@/components/base';
import StyledRangePicker from '@/components/DatePicker/StyledRangePicker';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { getTimezoneDayjs } from '@/utils/time';

import {
  DAILY_PRESETS,
  DASHBOARD_DATE_FILTER_TYPE,
  DASHBOARD_DATE_FILTER_UNIT,
  MONTHLY_PRESETS,
  WEEKLY_PRESETS,
} from './constants';
import type { DashboardDateFilterProps } from './types';
import { getAdjustDateRangeByUnit, getDateRange } from './utils';

const getPresets = (unit: DASHBOARD_DATE_FILTER_UNIT) => {
  switch (unit) {
    case DASHBOARD_DATE_FILTER_UNIT.DAY:
      return [
        ...DAILY_PRESETS.map((value) => ({
          label: value,
          value,
        })),
      ];
    case DASHBOARD_DATE_FILTER_UNIT.WEEK:
      return [
        ...WEEKLY_PRESETS.map((value) => ({
          label: value,
          value,
        })),
      ];
    case DASHBOARD_DATE_FILTER_UNIT.MONTH:
      return [
        ...MONTHLY_PRESETS.map((value) => ({
          label: value,
          value,
        })),
      ];
  }
};

export function DashboardDateFilter({
  value,
  onChange,
}: DashboardDateFilterProps) {
  const color = useColor();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const options: {
    label: DASHBOARD_DATE_FILTER_TYPE;
    value: DASHBOARD_DATE_FILTER_TYPE;
    onClick?: () => void;
  }[] = useMemo(() => {
    const defaults = [
      ...getPresets(value.unit),
      {
        label: DASHBOARD_DATE_FILTER_TYPE.CUSTOM,
        value: DASHBOARD_DATE_FILTER_TYPE.CUSTOM,
        onClick: () => {
          setCalendarOpen(true);
        },
      },
    ];

    return defaults;
  }, [value.unit]);

  const onUnitChange = (value: DASHBOARD_DATE_FILTER_UNIT) => {
    const type = getPresets(value)[0].value;
    const dateRange = getDateRange(type);
    onChange({
      unit: value,
      type: type,
      dateRange: dateRange,
    });
  };

  const pickerType = useMemo(() => {
    switch (value.unit) {
      case DASHBOARD_DATE_FILTER_UNIT.DAY:
        return 'date';
      case DASHBOARD_DATE_FILTER_UNIT.WEEK:
        return 'week';
      case DASHBOARD_DATE_FILTER_UNIT.MONTH:
        return 'month';
    }
  }, [value.unit]);

  const onDateRangeChange = (dates: RangePickerProps['value'] | null) => {
    if (dates) {
      const start = dates[0] || value.dateRange?.[0] || getTimezoneDayjs();
      const end = dates[1] || value.dateRange?.[1] || getTimezoneDayjs();
      const adjustedDateRange = getAdjustDateRangeByUnit(value.unit, [
        start,
        end,
      ]);
      onChange({
        unit: value.unit,
        type: DASHBOARD_DATE_FILTER_TYPE.CUSTOM,
        dateRange: adjustedDateRange,
      });
    }
    setCalendarOpen(false);
  };

  const onDateTypeChange = (e: RadioChangeEvent) => {
    if (e.target.value === DASHBOARD_DATE_FILTER_TYPE.CUSTOM) {
      setCalendarOpen(true);
    } else {
      setCalendarOpen(false);
    }
    const dateRange = getDateRange(e.target.value);
    onChange({
      unit: value.unit,
      type: e.target.value,
      dateRange: dateRange || value.dateRange,
    });
  };

  return (
    <VFlex a="flex-end" g={8}>
      <Flex g={8}>
        <Select
          style={{ width: 110 }}
          options={Object.values(DASHBOARD_DATE_FILTER_UNIT).map((value) => {
            return {
              label: value,
              value,
            };
          })}
          value={value.unit}
          onChange={onUnitChange}
        />
        <Radio.Group
          onChange={onDateTypeChange}
          value={value.type}
          optionType="button"
        >
          {options.map((opt) => (
            <Radio.Button
              key={opt.value}
              value={opt.value}
              onClick={() => {
                // To handle re-clicking the selected item
                if (opt.value === value.type) {
                  onDateTypeChange({
                    target: { value: opt.value },
                  } as RadioChangeEvent);
                }
                if (opt?.onClick) {
                  opt.onClick();
                }
              }}
            >
              {opt.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <StyledRangePicker
          hideTrigger
          open={calendarOpen}
          allowEmpty
          size={'middle'}
          picker={pickerType}
          value={value.dateRange}
          onChange={onDateRangeChange}
          style={{
            color: value.type === null ? color.primary100 : color.gray700,
            borderColor: value.type === null ? color.primary100 : color.gray300,
            backgroundColor:
              value.type === null
                ? getAlphaColor(color.primary100, 0.1)
                : 'transparent',
          }}
        />
      </Flex>
      <Typo type="label3" color="text-secondary">
        {value.dateRange?.[0].format('MMM D, YYYY')} -{' '}
        {value.dateRange?.[1].format('MMM D, YYYY')}
      </Typo>
    </VFlex>
  );
}
